import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Send, X } from 'lucide-react';
import {
  getEnvioAltaGruposJob,
  type EnvioLoteResumen,
} from '@lib/services/altas-masivas.service';
import { fmtNum, mensajeDeErrorEnvio } from '@lib/utils/envio-lote';

/**
 * Modal genérico de envío a Binary en lote (job async + polling del progreso).
 *
 * Lo usan las dos pantallas que despachan altas por API, con el MISMO criterio
 * de arranque distinto: "Legajos listos" envía una categoría entera y
 * "Rechazados" una selección explícita de legajos. Por eso el disparo se
 * inyecta (`iniciar`): acá adentro solo vive el ciclo job → polling → resumen.
 */
export function ModalOverlay(props: { children: React.ReactNode; onClose?: () => void }) {
  const { children, onClose } = props;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 37, 100, 0.4)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1050,
      }}
      onClick={(e) => {
        if (onClose && e.target === e.currentTarget) onClose();
      }}
    >
      {children}
    </div>
  );
}

export function EnvioProgreso(props: {
  resumen?: EnvioLoteResumen;
  cargando: boolean;
  enCurso: boolean;
  onCerrar: () => void;
}) {
  const { resumen, cargando, enCurso, onCerrar } = props;
  if (!resumen) {
    return <p className="text-element-mid">{cargando ? 'Cargando…' : 'Sin datos del envío.'}</p>;
  }
  const pct =
    resumen.totalGrupos > 0 ? Math.round((resumen.procesados / resumen.totalGrupos) * 100) : 0;
  const fallidos = (resumen.resultados ?? []).filter((r) => r.estado === 'error');
  return (
    <>
      <div>
        <div className="rowBetween" style={{ fontSize: 13, marginBottom: 6 }}>
          <span>
            {enCurso ? 'Enviando…' : 'Terminado'} — {fmtNum(resumen.procesados)} /{' '}
            {fmtNum(resumen.totalGrupos)}
          </span>
          <span>{pct}%</span>
        </div>
        <div
          style={{
            height: 8,
            borderRadius: 6,
            background: 'var(--color-surface-accent)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: enCurso ? '#7C3AED' : '#166534',
              transition: 'width .3s',
            }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 13, flexWrap: 'wrap' }}>
        <span style={{ color: '#166534' }}>
          <strong>{fmtNum(resumen.exitosos)}</strong> dados de alta
        </span>
        <span style={{ color: '#B91C1C' }}>
          <strong>{fmtNum(resumen.fallidos)}</strong> rechazados
        </span>
        <span className="text-element-mid">
          <strong>{fmtNum(resumen.omitidos)}</strong> ya estaban de alta
        </span>
      </div>
      {resumen.estado === 'error' && resumen.error && (
        <p className="status status-error" style={{ padding: '8px 12px' }}>
          El envío abortó: {resumen.error}
        </p>
      )}
      {fallidos.length > 0 && (
        <div className="tableWrap" style={{ overflow: 'auto', maxHeight: 260 }}>
          <table className="dataTable">
            <thead>
              <tr>
                <th>Legajo</th>
                <th>Titular</th>
                <th>Motivo del rechazo</th>
              </tr>
            </thead>
            <tbody>
              {fallidos.map((r, i) => (
                <tr key={`${r.legajo}-${i}`}>
                  <td>
                    <strong>{r.legajo}</strong>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {r.titular.apellido} {r.titular.nombres}
                  </td>
                  <td style={{ fontSize: 12 }}>{r.error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-primary" onClick={onCerrar} disabled={enCurso}>
          {enCurso ? (
            <>
              <span className="spinner" /> Procesando…
            </>
          ) : (
            'Cerrar'
          )}
        </button>
      </div>
    </>
  );
}

export function EnvioLoteModal(props: {
  titulo: string;
  /** Texto de confirmación previo al disparo. */
  confirmacion: React.ReactNode;
  /** Cantidad a enviar (solo para el label del botón). */
  cantidad: number;
  /** Dispara el job en el backend y devuelve el resumen con el jobId. */
  iniciar: () => Promise<EnvioLoteResumen>;
  onCerrar: () => void;
  /** Se llama en cada tick de progreso (para refrescar contadores de fondo). */
  onProgreso?: () => void;
  /** Se llama una vez al terminar (o abortar) el job. */
  onFin?: () => void;
}) {
  const { titulo, confirmacion, cantidad, iniciar, onCerrar, onProgreso, onFin } = props;
  const [jobId, setJobId] = useState<string | null>(null);
  const [iniciando, setIniciando] = useState(false);
  const [errorInicio, setErrorInicio] = useState<string | null>(null);

  const jobQ = useQuery({
    queryKey: ['envio-alta-grupos-job', jobId],
    queryFn: () => getEnvioAltaGruposJob(jobId as string),
    enabled: !!jobId,
    // Poll cada 2s mientras corre; para al terminar (o error).
    refetchInterval: (query) => {
      const st = query.state.data?.estado;
      return st === 'terminado' || st === 'error' ? false : 2000;
    },
  });
  const resumen = jobQ.data;
  const enCurso = !!jobId && resumen?.estado !== 'terminado' && resumen?.estado !== 'error';

  useEffect(() => {
    if (!resumen) return;
    if (resumen.estado === 'terminado' || resumen.estado === 'error') onFin?.();
    else onProgreso?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumen?.procesados, resumen?.estado]);

  async function disparar() {
    setIniciando(true);
    setErrorInicio(null);
    try {
      const r = await iniciar();
      setJobId(r.jobId);
    } catch (e) {
      setErrorInicio(mensajeDeErrorEnvio(e));
    } finally {
      setIniciando(false);
    }
  }

  return (
    <ModalOverlay onClose={enCurso ? undefined : onCerrar}>
      <div
        className="card"
        role="dialog"
        aria-modal="true"
        style={{
          maxWidth: 640,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          maxHeight: '85vh',
        }}
      >
        <header className="rowBetween" style={{ alignItems: 'center' }}>
          <h3 className="heading-5" style={{ margin: 0 }}>
            {titulo}
          </h3>
          {!enCurso && (
            <button type="button" className="modalClose" onClick={onCerrar} aria-label="Cerrar">
              <X size={18} />
            </button>
          )}
        </header>

        {!jobId && (
          <>
            {confirmacion}
            {errorInicio && (
              <p className="status status-error" style={{ padding: '8px 12px' }}>
                {errorInicio}
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn btn-ghost" onClick={onCerrar} disabled={iniciando}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => disparar()}
                disabled={iniciando}
              >
                {iniciando ? (
                  <>
                    <span className="spinner" /> Iniciando…
                  </>
                ) : (
                  <>
                    <Send size={16} /> Enviar {fmtNum(cantidad)}
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {jobId && (
          <EnvioProgreso
            resumen={resumen}
            cargando={jobQ.isLoading}
            enCurso={enCurso}
            onCerrar={onCerrar}
          />
        )}
      </div>
    </ModalOverlay>
  );
}
